import { create } from "zustand";

export type ProjectSaveTarget = "local-download" | "cloud" | "browser-storage" | "import";
export type ProjectSaveStatus = "clean" | "dirty" | "saving" | "error";

type ProjectSaveState = {
  lastSavedSnapshotHash: string | null;
  saveStatus: ProjectSaveStatus;
  lastSavedAt: string | null;
  lastSaveTarget: ProjectSaveTarget | null;
  lastSaveError: string | null;
};

type ProjectSaveActions = {
  hasUnsavedChanges: (
    currentSnapshotHash: string | null,
    hasMeaningfulProjectWork: boolean
  ) => boolean;
  markProjectSaved: (snapshotHash: string, target: ProjectSaveTarget) => void;
  markProjectDirty: () => void;
  markProjectSaving: (target?: ProjectSaveTarget) => void;
  markProjectSaveError: (error: unknown) => void;
  resetProjectSaveState: () => void;
};

const initialState: ProjectSaveState = {
  lastSavedSnapshotHash: null,
  saveStatus: "clean",
  lastSavedAt: null,
  lastSaveTarget: null,
  lastSaveError: null,
};

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error || "Project save failed");

const useProjectSaveStoreInternal = create<ProjectSaveState & { actions: ProjectSaveActions }>(
  (set, get) => ({
    ...initialState,
    actions: {
      hasUnsavedChanges: (currentSnapshotHash, hasMeaningfulProjectWork) => {
        if (!hasMeaningfulProjectWork) return false;
        const status = get().saveStatus;
        if (status === "dirty" || status === "error") return true;
        const lastSavedSnapshotHash = get().lastSavedSnapshotHash;
        if (!lastSavedSnapshotHash) return true;
        if (!currentSnapshotHash) return false;
        return currentSnapshotHash !== lastSavedSnapshotHash;
      },
      markProjectSaved: (snapshotHash, target) => {
        set({
          lastSavedSnapshotHash: snapshotHash,
          saveStatus: "clean",
          lastSavedAt: new Date().toISOString(),
          lastSaveTarget: target,
          lastSaveError: null,
        });
      },
      markProjectDirty: () => {
        set({ saveStatus: "dirty", lastSaveError: null });
      },
      markProjectSaving: (target) => {
        set({ saveStatus: "saving", lastSaveTarget: target ?? get().lastSaveTarget });
      },
      markProjectSaveError: (error) => {
        set({ saveStatus: "error", lastSaveError: errorMessage(error) });
      },
      resetProjectSaveState: () => {
        set({ ...initialState });
      },
    },
  })
);

export const useProjectSaveStatus = () => useProjectSaveStoreInternal((state) => state.saveStatus);
export const useLastSavedSnapshotHash = () =>
  useProjectSaveStoreInternal((state) => state.lastSavedSnapshotHash);
export const useLastSavedAt = () => useProjectSaveStoreInternal((state) => state.lastSavedAt);
export const useLastSaveTarget = () => useProjectSaveStoreInternal((state) => state.lastSaveTarget);
export const getProjectSaveActions = () => useProjectSaveStoreInternal.getState().actions;
export const getProjectSaveState = () => {
  const { actions: _actions, ...state } = useProjectSaveStoreInternal.getState();
  return state;
};
